import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import * as d3 from "d3";
import { Grid, Paper } from "@mui/material";
import { Constants } from "constants/constant";
import { downloadChartImage } from 'utilities/downloadChartImage';
import {
  layoutLegend,
  renderLegendInto,
  CHART_PALETTE,
  CHART_MIN_WIDTH,
  CHART_AXIS_LABEL_OFFSET,
  CHART_LEGEND_TOP_GAP,
  CHART_BOTTOM_PAD,
} from "utilities/chartLegendLayout";
import {
  escapeHtml,
  positionTooltip,
  TOOLTIP_STYLE_REACT,
} from "utilities/chartTooltip";

type RawData = {
  year: string;
  [key: string]: number | string;
};

type ProcessedData = {
  year: Date;
  [key: string]: number | Date;
};

type ConsolidatedStackedAreaChatProps = {
  data: RawData[];
  width: number;
  height: number;
  chartType: "TargetTrajectory" | "TimelineAndFinancialMetrics";
};

export type ConsolidatedStackedAreaChatRef = {
  downloadChart: (format: "png" | "jpeg") => void;
};

const MARGIN = { top: 24, right: 24, left: 110 };

const ConsolidatedStackedAreaChat = forwardRef<ConsolidatedStackedAreaChatRef, ConsolidatedStackedAreaChatProps>(
  ({ data, width, height, chartType }, ref) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState(width);
  const UNIT_TCO2E = Constants.UNIT_TCO2E;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const update = () =>
      setMeasuredWidth(Math.max(element.clientWidth || width, CHART_MIN_WIDTH));
    update();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [width]);

  const keys = useMemo(
    () => (data.length > 0 ? Object.keys(data[0]).filter((d) => d !== "year") : []),
    [data]
  );

  const color = useMemo(
    () => d3.scaleOrdinal(CHART_PALETTE).domain(keys),
    [keys]
  );

  const chartWidth = measuredWidth;
  const innerWidth = chartWidth - MARGIN.left - MARGIN.right;
  const plotHeight = Math.max(200, height - 80);

  const legend = useMemo(
    () =>
      layoutLegend(
        keys.map((key) => ({ label: key, color: color(key) })),
        innerWidth
      ),
    [keys, color, innerWidth]
  );

  const legendTop =
    MARGIN.top + plotHeight + CHART_AXIS_LABEL_OFFSET + CHART_LEGEND_TOP_GAP;
  // The legend is the last thing drawn, so the canvas ends just below it.
  const totalHeight = legendTop + legend.height + CHART_BOTTOM_PAD;

  // --- Download Handler ---
  const handleDownload = (format: "png" | "jpeg") => {
    if (!svgRef.current) {
      console.warn("SVG reference is missing.");
      return;
    }
    // The SVG already carries the legend, so the exporter only has to rasterise
    // it. This keeps the export identical to the screen.
    downloadChartImage({
      svgRef,
      format,
      fileName: `${chartType === "TargetTrajectory" ? "target-trajectory" : "decarbonization-pathway"}-chart.${format}`,
    });
  };

  // Expose downloadChart via ref
  useImperativeHandle(ref, () => ({
    downloadChart: handleDownload,
    _testSvgRef: svgRef
  }));

  useEffect(() => {
    if (!svgRef.current) return;

    // Clear first: an empty update must wipe the previous render rather than
    // leaving a stale chart on screen.
    d3.select(svgRef.current).selectAll("*").remove();
    if (!data.length) return;

    const svg = d3
      .select(svgRef.current)
      .attr("width", chartWidth)
      .attr("height", totalHeight)
      .attr("viewBox", `0 0 ${chartWidth} ${totalHeight}`);

    const parseYear = d3.timeParse("%Y");
    const formatYear = d3.timeFormat("%Y");

    const processedData: ProcessedData[] = data.map((d) => ({
      ...d,
      year: parseYear(d.year) as Date,
    }));

    const stack = d3
      .stack<ProcessedData>()
      .keys(keys)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

    const series = stack(processedData);

    const x = d3
      .scaleTime()
      .domain(d3.extent(processedData, (d) => d.year) as [Date, Date])
      .range([0, innerWidth]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(series, (d) => d3.max(d, (d) => d[1])) as number])
      .nice()
      .range(
        chartType === "TargetTrajectory"
          ? [plotHeight, 10]
          : [0, plotHeight]
      );

    const g = svg
      .append("g")
      .attr("class", "chart-plot")
      .attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

    const area = d3
      .area<d3.SeriesPoint<ProcessedData>>()
      .x((d) => x(d.data.year))
      .y0((d) => y(d[0]))
      .y1((d) => y(d[1]));

    g.append("g")
      .selectAll("path")
      .data(series)
      .enter()
      .append("path")
      .attr("fill", (d) => color(d.key))
      .attr("d", area)
      .on("mouseover", function (event, d) {
        const year = x.invert(d3.pointer(event, this)[0]);
        const tooltipData = processedData.find(
          (dataPoint) => formatYear(dataPoint.year) === formatYear(year)
        );

        let tooltipContent = `<div style="font-weight:600;margin-bottom:6px">Year: ${escapeHtml(
          formatYear(year)
        )}</div>`;
        if (tooltipData) {
          keys.forEach((key) => {
            tooltipContent +=
              `<div style="display:flex;gap:8px;align-items:baseline">` +
              `<span style="flex:0 0 10px;height:10px;border-radius:50%;background:${escapeHtml(
                color(key)
              )}"></span>` +
              `<span>${escapeHtml(key)}: ${escapeHtml(
                tooltipData[key]
              )}</span></div>`;
          });
        }

        d3.select(tooltipRef.current).html(tooltipContent).style("opacity", 1);
        positionTooltip(tooltipRef.current, event);
      })
      .on("mousemove", function (event) {
        positionTooltip(tooltipRef.current, event);
      })
      .on("mouseout", function () {
        d3.select(tooltipRef.current).style("opacity", 0);
      });

    // Append the x-axis
    g.append("g")
      .attr("class", "axis axis--x")
      .attr("transform", `translate(0,${plotHeight})`)
      .call(d3.axisBottom(x).tickFormat(formatYear as any))
      .selectAll("text")
      .style("font-size", "12px")
      .style("fill", "#7A7A9D");

    // Append the y-axis
    g.append("g")
      .attr("class", "axis axis--y")
      .call(d3.axisLeft(y).ticks(10).tickPadding(10))
      .selectAll("text")
      .style("font-size", "12px")
      .style("fill", "#7A7A9D");

    // Append the y-axis label
    g.append("text")
      .attr("class", "axis-label")
      .attr("transform", "rotate(-90)")
      .attr("x", -plotHeight / 2)
      .attr("y", -MARGIN.left / 1.5)
      .style("text-anchor", "middle")
      .text(
        chartType === "TargetTrajectory"
          ? "GHG Emissions Reduction"
          : `Annual Emission Reduction (${UNIT_TCO2E} / yr)`
      )
      .style("font-size", "18px")
      .style("fill", "#425466");

    g.append("text")
      .attr("class", "axis-label")
      .attr("x", innerWidth / 2)
      .attr("y", plotHeight + CHART_AXIS_LABEL_OFFSET)
      .attr("text-anchor", "middle")
      .text("Year")
      .style("font-size", "18px")
      .style("fill", "#425466");

    // Legend: fixed columns beneath the plot, sharing the plot's width.
    renderLegendInto(svg, legend, MARGIN.left, legendTop);

  }, [
    data,
    keys,
    color,
    chartType,
    UNIT_TCO2E,
    chartWidth,
    innerWidth,
    plotHeight,
    totalHeight,
    legend,
    legendTop,
  ]);

  return (
    <Grid item xs={12} sx={{ mb: 5 }}>
      <Paper>
        <div ref={containerRef} style={{ width: "100%" }}>
          <svg ref={svgRef} style={{ maxWidth: "100%", height: "auto" }}></svg>
          {/* Portalled to <body> so the page coordinates written by
              positionTooltip are not offset by a positioned ancestor. */}
          {createPortal(
            <div
              ref={tooltipRef}
              style={{ ...TOOLTIP_STYLE_REACT, opacity: 0 }}
            />,
            document.body
          )}
        </div>
      </Paper>
    </Grid>
  );
});

export default ConsolidatedStackedAreaChat;